import { useParams } from "react-router-dom";
import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { usePriceMap } from "@/hooks/usePrices";
import { img } from "@/lib/images";

// All sidebar links for wedding sub-navigation
const weddingSidebarLinks = [
  { slug: "general-wedding-information", key: "allgemein" },
  { slug: "wedding-with-registry-office", key: "standesamt" },
  { slug: "wedding-without-registry-office", key: "ohne_standesamt" },
  { slug: "vow-renewal", key: "eheversprechen" },
  { slug: "wedding-anniversary", key: "jubilaeum" },
];

// Mapping of add-on index -> central price key (used for dynamic pricing)
const ADDON_PRICE_KEYS: Record<string, string> = {
  "1": "wedding_addon_photographer",
  "2": "wedding_addon_photosession",
  "3": "wedding_addon_candle_dinner",
  "4": "wedding_addon_cake",
  "5": "wedding_addon_hairdresser",
  "7": "wedding_addon_honeymooner",
  "8": "wedding_addon_extra_villa",
};

// Configuration for each sub-page
const weddingData: Record<
  string,
  {
    headerImage: string;
    sections: { titleKey: string; items?: string[]; paragraphs?: string[] }[];
    priceKey?: string;
    isPackage?: boolean;
  }
> = {
  "general-wedding-information": {
    headerImage: img("weddings/general-wedding-information/header-general-wedding-information-01.jpg"),
    sections: [
      {
        titleKey: "wedding_allgemein_sec1_title",
        paragraphs: ["wedding_allgemein_sec1_p1", "wedding_allgemein_sec1_p2", "wedding_allgemein_sec1_p3"],
        items: [
          "wedding_allgemein_doc_1",
          "wedding_allgemein_doc_2",
          "wedding_allgemein_doc_3",
          "wedding_allgemein_doc_4",
          "wedding_allgemein_doc_5",
          "wedding_allgemein_doc_6",
          "wedding_allgemein_doc_7",
          "wedding_allgemein_doc_8",
          "wedding_allgemein_doc_9",
          "wedding_allgemein_doc_10",
        ],
      },
      {
        titleKey: "wedding_allgemein_sec1_footnote_title",
        paragraphs: ["wedding_allgemein_sec1_footnote"],
      },
      {
        titleKey: "wedding_allgemein_sec1_closing_title",
        paragraphs: ["wedding_allgemein_sec1_closing"],
      },
      {
        titleKey: "wedding_allgemein_sec2_title",
        paragraphs: ["wedding_allgemein_sec2_p1", "wedding_allgemein_sec2_p2"],
      },
      {
        titleKey: "wedding_allgemein_sec3_title",
        paragraphs: ["wedding_allgemein_sec3_p1"],
      },
    ],
  },
  "wedding-with-registry-office": {
    headerImage: img("weddings/wedding-with-registry-office/header-weddings-with-registry-office-01.jpg"),
    priceKey: "wedding_standesamt",
    isPackage: true,
    sections: [
      {
        titleKey: "wedding_standesamt_villa_title",
        items: [
          "wedding_standesamt_villa_1",
          "wedding_standesamt_villa_2",
          "wedding_standesamt_villa_3",
          "wedding_standesamt_villa_4",
          "wedding_standesamt_villa_5",
          "wedding_standesamt_villa_6",
          "wedding_standesamt_villa_7",
        ],
      },
      {
        titleKey: "wedding_standesamt_docs_title",
        items: [
          "wedding_standesamt_docs_1",
          "wedding_standesamt_docs_2",
          "wedding_standesamt_docs_3",
        ],
      },
      {
        titleKey: "wedding_standesamt_legal_title",
        items: [
          "wedding_standesamt_legal_1",
          "wedding_standesamt_legal_2",
          "wedding_standesamt_legal_3",
        ],
      },
      {
        titleKey: "wedding_standesamt_service_title",
        paragraphs: ["wedding_standesamt_service_note"],
      },
      {
        titleKey: "wedding_standesamt_extra_title",
        items: [
          "wedding_standesamt_extra_1",
        ],
        paragraphs: ["wedding_standesamt_extra_note"],
      },
      {
        titleKey: "wedding_standesamt_addons_title",
        paragraphs: ["wedding_standesamt_addons_note"],
        items: [
          "wedding_standesamt_addon_1",
          "wedding_standesamt_addon_2",
          "wedding_standesamt_addon_3",
          "wedding_standesamt_addon_4",
          "wedding_standesamt_addon_5",
          "wedding_standesamt_addon_6",
          "wedding_standesamt_addon_7",
          "wedding_standesamt_addon_8",
        ],
      },
    ],
  },
  "wedding-without-registry-office": {
    headerImage: img("weddings/wedding-without-registry-office/header-weddings-without-registry-office-01.jpg"),
    priceKey: "wedding_ohne_standesamt",
    isPackage: true,
    sections: [
      {
        titleKey: "wedding_ohne_villa_title",
        items: [
          "wedding_ohne_villa_1",
          "wedding_ohne_villa_2",
          "wedding_ohne_villa_3",
          "wedding_ohne_villa_4",
          "wedding_ohne_villa_5",
          "wedding_ohne_villa_6",
          "wedding_ohne_villa_7",
        ],
      },
      {
        titleKey: "wedding_ohne_ceremony_title",
        items: [
          "wedding_ohne_ceremony_1",
          "wedding_ohne_ceremony_2",
        ],
      },
      {
        titleKey: "wedding_ohne_service_title",
        paragraphs: ["wedding_ohne_service_note"],
      },
      {
        titleKey: "wedding_ohne_addons_title",
        paragraphs: ["wedding_ohne_addons_note"],
        items: [
          "wedding_ohne_addon_1",
          "wedding_ohne_addon_2",
          "wedding_ohne_addon_3",
          "wedding_ohne_addon_4",
          "wedding_ohne_addon_5",
          "wedding_ohne_addon_6",
          "wedding_ohne_addon_7",
          "wedding_ohne_addon_8",
        ],
      },
    ],
  },
  "vow-renewal": {
    headerImage: img("weddings/vow-renewal/header-wedding-vow-renewal-01.jpg"),
    priceKey: "wedding_eheversprechen",
    isPackage: true,
    sections: [
      {
        titleKey: "wedding_eheversprechen_villa_title",
        items: [
          "wedding_eheversprechen_villa_1",
          "wedding_eheversprechen_villa_2",
          "wedding_eheversprechen_villa_3",
          "wedding_eheversprechen_villa_4",
          "wedding_eheversprechen_villa_5",
          "wedding_eheversprechen_villa_6",
          "wedding_eheversprechen_villa_7",
        ],
      },
      {
        titleKey: "wedding_eheversprechen_ceremony_title",
        items: [
          "wedding_eheversprechen_ceremony_1",
          "wedding_eheversprechen_ceremony_2",
        ],
      },
      {
        titleKey: "wedding_eheversprechen_service_title",
        paragraphs: ["wedding_eheversprechen_service_note"],
      },
      {
        titleKey: "wedding_eheversprechen_addons_title",
        paragraphs: ["wedding_eheversprechen_addons_note"],
        items: [
          "wedding_eheversprechen_addon_1",
          "wedding_eheversprechen_addon_2",
          "wedding_eheversprechen_addon_3",
          "wedding_eheversprechen_addon_4",
          "wedding_eheversprechen_addon_5",
          "wedding_eheversprechen_addon_6",
          "wedding_eheversprechen_addon_7",
          "wedding_eheversprechen_addon_8",
        ],
      },
    ],
  },
  "wedding-anniversary": {
    headerImage: img("weddings/wedding-anniversary/header-wedding-anniversary-01.jpg"),
    priceKey: "wedding_jubilaeum",
    isPackage: true,
    sections: [
      {
        titleKey: "wedding_jubilaeum_villa_title",
        items: [
          "wedding_jubilaeum_villa_1",
          "wedding_jubilaeum_villa_2",
          "wedding_jubilaeum_villa_3",
          "wedding_jubilaeum_villa_4",
          "wedding_jubilaeum_villa_5",
          "wedding_jubilaeum_villa_6",
          "wedding_jubilaeum_villa_7",
        ],
      },
      {
        titleKey: "wedding_jubilaeum_ceremony_title",
        items: [
          "wedding_jubilaeum_ceremony_1",
          "wedding_jubilaeum_ceremony_2",
        ],
      },
      {
        titleKey: "wedding_jubilaeum_service_title",
        paragraphs: ["wedding_jubilaeum_service_note"],
      },
      {
        titleKey: "wedding_jubilaeum_addons_title",
        paragraphs: ["wedding_jubilaeum_addons_note"],
        items: [
          "wedding_jubilaeum_addon_1",
          "wedding_jubilaeum_addon_2",
          "wedding_jubilaeum_addon_3",
          "wedding_jubilaeum_addon_4",
          "wedding_jubilaeum_addon_5",
          "wedding_jubilaeum_addon_6",
          "wedding_jubilaeum_addon_7",
          "wedding_jubilaeum_addon_8",
        ],
      },
    ],
  },
};

export default function WeddingDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { priceMap, money } = usePriceMap();

  const data = slug ? weddingData[slug] : undefined;
  const currentIndex = weddingSidebarLinks.findIndex((l) => l.slug === slug);
  const prevLink = currentIndex > 0 ? weddingSidebarLinks[currentIndex - 1] : null;
  const nextLink =
    currentIndex < weddingSidebarLinks.length - 1
      ? weddingSidebarLinks[currentIndex + 1]
      : null;

  if (!data) {
    return (
      <div className="bg-background-50 text-foreground-950 min-h-screen">
        <Navbar />
        <main className="flex items-center justify-center py-24">
          <div className="text-center">
            <h1 className="font-heading text-3xl mb-4">{t("weddings_title")}</h1>
            <LocalizedLink to="/weddings" className="text-accent-700 hover:underline">
              ← {t("wedding_back_to_overview")}
            </LocalizedLink>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catKey = weddingSidebarLinks.find((l) => l.slug === slug)?.key ?? "";

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("weddings_eyebrow")}
          title={t(`wedding_cat_${catKey}_title`)}
          subtitle={t(`wedding_cat_${catKey}_header_sub`)}
          image={data.headerImage}
          imageAlt={t(`wedding_cat_${catKey}_title`)}
        />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
              {/* Main content */}
              <div className="lg:col-span-3">
                {/* Back link */}
                <LocalizedLink
                  to="/weddings"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-700 mb-8 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("wedding_back_to_overview")}
                </LocalizedLink>

                {/* Price badge for package pages */}
                {data.isPackage && data.priceKey && (
                  <div className="bg-accent-100 border border-accent-200 rounded-lg p-5 mb-8 flex items-center gap-4">
                    <i className="ri-price-tag-3-line text-2xl text-accent-700"></i>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-accent-600 mb-0.5">
                        {t("wedding_price_label")}
                      </div>
                      <div className="font-heading text-xl text-accent-700 font-semibold">
                        {money(priceMap[data.priceKey])}
                      </div>
                      <div className="text-xs text-foreground-600 mt-0.5">
                        {t("wedding_price_includes")}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sections */}
                {data.sections.map((section, si) => (
                  <div key={si} className="mb-8">
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4 pb-2 border-b border-background-200">
                      {t(section.titleKey)}
                    </h2>
                    {section.paragraphs?.map((pKey, pi) => (
                      <p key={pi} className="text-foreground-700 leading-relaxed mb-3 text-sm">
                        {t(pKey)}
                      </p>
                    ))}
                    {section.items && section.items.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {section.items.map((itemKey, ii) => {
                          const addonNum = itemKey.match(/_addon_(\d+)$/)?.[1];
                          const addonPriceKey = addonNum ? ADDON_PRICE_KEYS[addonNum] : undefined;
                          return (
                            <li key={ii} className="flex items-start gap-2.5 text-sm text-foreground-700">
                              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-xs"></i>
                              </span>
                              {addonPriceKey
                                ? t(itemKey, { price: money(priceMap[addonPriceKey]) })
                                : t(itemKey)}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}

                {/* Enquire CTA */}
                <div className="mt-10 pt-8 border-t border-background-200">
                  <LocalizedLink
                    to="/wedding-inquiry"
                    className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 px-7 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-mail-send-line"></i>
                    {t("weddings_enquire")}
                  </LocalizedLink>
                </div>

                {/* Prev / Next navigation */}
                <div className="mt-10 pt-6 border-t border-background-200 flex items-center justify-between flex-wrap gap-3">
                  {prevLink ? (
                    <LocalizedLink
                      to={`/weddings/${prevLink.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>
                      {t(`wedding_cat_${prevLink.key}_title`)}
                    </LocalizedLink>
                  ) : (
                    <span />
                  )}
                  {nextLink ? (
                    <LocalizedLink
                      to={`/weddings/${nextLink.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                    >
                      {t(`wedding_cat_${nextLink.key}_title`)}
                      <i className="ri-arrow-right-line"></i>
                    </LocalizedLink>
                  ) : (
                    <span />
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-4">
                  {/* Hochzeit sub-nav */}
                  <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                    <div className="bg-background-100 px-4 py-3 text-xs uppercase tracking-wider font-semibold text-foreground-700 border-b border-background-200">
                      {t("wedding_sidebar_offers")}
                    </div>
                    {weddingSidebarLinks.map((link) => (
                      <LocalizedLink
                        key={link.slug}
                        to={`/weddings/${link.slug}`}
                        className={`block px-4 py-2.5 text-sm border-b border-background-100 last:border-0 transition-colors ${
                          slug === link.slug
                            ? "bg-accent-50 text-accent-700 font-medium"
                            : "text-foreground-700 hover:bg-accent-100 hover:text-accent-700"
                        }`}
                      >
                        {t(`wedding_cat_${link.key}_title`)}
                      </LocalizedLink>
                    ))}
                  </div>

                  {/* Contact box */}
                  <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                    <div className="bg-background-100 px-4 py-3 text-xs uppercase tracking-wider font-semibold text-foreground-700 border-b border-background-200">
                      {t("contact_title")}
                    </div>
                    <div className="px-4 py-4 text-sm text-foreground-600 space-y-3">
                      <p className="text-xs leading-relaxed">{t("wedding_sidebar_contact_note")}</p>
                      <a
                        href="tel:+6281246218000"
                        className="flex items-center gap-2 text-foreground-700 hover:text-accent-700 transition-colors"
                      >
                        <i className="ri-phone-line text-accent-600"></i>
                        +62 81 246 21 8000
                      </a>
                      <a
                        href="mailto:office@balivwtour.com"
                        className="flex items-center gap-2 text-foreground-700 hover:text-accent-700 transition-colors"
                      >
                        <i className="ri-mail-line text-accent-600"></i>
                        office@balivwtour.com
                      </a>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

